import React, { useEffect, useState } from 'react';
import { CustomButton } from '../Button/Button';
import CloseButton from '../CloseButton/CloseButton';
import styles from './VideoSpeedUpEditor.module.scss';
import { useTranslation } from 'react-i18next';
import { useVideoSpeedUpContext } from '../../context/VideoSpeedUpContext';
import Slider from 'rc-slider';
import usePrevious from '../../../common/hooks/usePrevious';

const minSpeed = 1;
const maxSpeed = 4;

const VideoSpeedUpEditor = ({ activeScene, currentVideoPart, handleSpeedUpChange, miniSidebar, closeFeatures }) => {
  const { speedUpValue, changeSpeedUpValue, toggleVideoSpeedUpOpened } = useVideoSpeedUpContext();

  const [initialSpeedUpValue, setInitialSpeedUpValue] = useState(1);

  const previousVideoPart = usePrevious(currentVideoPart);

  const { t } = useTranslation();

  const handleSaveChanges = () => {
    toggleVideoSpeedUpOpened();
    handleSpeedUpChange({
      sceneIndex: Number(currentVideoPart),
      playbackRate: speedUpValue,
      initialSpeedUpValue,
      registerAction: true,
    });
  };

  const handleChangeSlider = value => {
    if (activeScene?.id === 'Intro' || activeScene?.id === 'Outro') {
      return;
    }

    changeSpeedUpValue(value);
    handleSpeedUpChange({
      sceneIndex: Number(currentVideoPart),
      playbackRate: value,
    });
  };

  useEffect(() => {
    if (miniSidebar) {
      handleClose();
    }
  }, [miniSidebar]);

  useEffect(() => {
    if (closeFeatures) {
      handleClose();
    }
  }, [closeFeatures]);

  const handleClose = () => {
    toggleVideoSpeedUpOpened();
    handleSpeedUpChange({
      sceneIndex: Number(currentVideoPart),
      playbackRate: initialSpeedUpValue,
    });
  };

  useEffect(() => {
    // Save changes with registration action after switching between scenes
    if (previousVideoPart && currentVideoPart && previousVideoPart !== currentVideoPart && initialSpeedUpValue !== speedUpValue) {
      handleSpeedUpChange({
        sceneIndex: Number(previousVideoPart),
        playbackRate: speedUpValue,
        initialSpeedUpValue,
        registerAction: true,
      });
    }
  }, [currentVideoPart]);

  useEffect(() => {
    if (activeScene) {
      changeSpeedUpValue(activeScene.playbackRate || 1);
    }
  }, [activeScene?.playbackRate]);

  useEffect(() => {
    if (activeScene?.id) {
      setInitialSpeedUpValue(activeScene.playbackRate || 1);
    }
  }, [activeScene?.id]);

  return (
    <div className={styles.wrapper}>
      <div className={styles.mainContainer}>
        <div className={styles.headerContainer}>
          <span>{t('my_video.tools.speed.title')}</span>
          <CloseButton onClose={handleClose} />
        </div>
        <div className={styles.sliderContainer}>
          <span className={styles.minMaxValue}>{minSpeed}x</span>
          <Slider
            min={minSpeed}
            max={maxSpeed}
            step={0.25}
            onChange={value => handleChangeSlider(value)}
            value={speedUpValue}
            dots={true}
            included
            style={{ width: '350px' }}
            handleStyle={{
              width: '18px',
              height: '18px',
              background: '#FFFFFF',
              position: 'relative',
              top: '-10px',
              borderRadius: '9px',
              boxShadow: '0px 0px 10px rgba(0, 0, 0, 0.3)',
              cursor: 'pointer',
            }}
            trackStyle={{
              background: '#37C1A0',
              height: '4px',
              borderRadius: '2px',
              position: 'relative',
              top: '-4px',
            }}
            railStyle={{
              background: '#EEEEEE',
              height: '4px',
              borderRadius: '2px',
              position: 'relative',
            }}
            dotStyle={{
              marginLeft: '2px',
              color: '#37c1a0',
              bottom: '6px',
            }}
            activeDotStyle={{
              borderColor: '#37c1a0',
              backgroundColor: '#37c1a0',
            }}
          />
          <span className={styles.minMaxValue}>{maxSpeed}x</span>
          <div className={styles.currentValueContainer}>
            <div
              className={styles.currentValue}
              style={{ left: `calc(${((speedUpValue - minSpeed) / (maxSpeed - minSpeed)) * 100}%)` }}
            >
              <div className={styles.triangleDown}></div>

              {speedUpValue}
            </div>
          </div>
        </div>
      </div>
      <div className={styles.footer}>
        <CustomButton
          onClick={handleSaveChanges}
          disabled={initialSpeedUpValue === speedUpValue}
          className={styles.saveBtn}
          label={t('general.save')}
          variant="primary"
        />
      </div>
    </div>
  );
};

export default VideoSpeedUpEditor;
